import { productBelongsToSport, SPORTS } from './sports';

export function brandToSlug(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getProductBrandSlug(product) {
  const slug = String(product?.brand_slug || '').trim();
  if (slug) return slug;

  const legacy = String(product?.license_brand || '').trim();
  if (legacy) return brandToSlug(legacy);

  return '';
}

function sameLabel(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

/** Licensed brand name (Puma, Adidas…) — never the team, even if products.brand was copied from team. */
export function getProductBrandName(product, brands = []) {
  if (!product) return '';

  const slug = getProductBrandSlug(product);
  if (slug) {
    const match = findBrandBySlug(slug, brands);
    if (match?.name) return match.name;

    const fromSlug = slug
      .split('-')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
    if (fromSlug) return fromSlug;
  }

  const license = String(product.license_brand || '').trim();
  if (license) return license;

  const brand = String(product.brand || '').trim();
  const team = String(product.team || '').trim();
  if (brand && !sameLabel(brand, team)) return brand;

  return '';
}

export function productBelongsToBrand(product, brandSlug) {
  if (!brandSlug) return false;
  return getProductBrandSlug(product) === brandSlug;
}

export function findBrandBySlug(brandSlug, brands = []) {
  const slug = String(brandSlug || '').trim();
  if (!slug) return null;
  return (brands || []).find(brand => brand.slug === slug) || null;
}

/** Admin brands that have at least one product — for footer links. */
export function getFooterBrands(brands, products) {
  const slugsWithProducts = new Set(
    (products || []).map(product => getProductBrandSlug(product)).filter(Boolean)
  );

  return (brands || [])
    .filter(brand => brand.visible !== false && slugsWithProducts.has(brand.slug))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
}

/** Sports this brand has products in. */
export function getSportsForBrand(products, brandSlug) {
  if (!brandSlug) return [];

  const sportSlugs = new Set();

  (products || []).forEach(product => {
    if (!productBelongsToBrand(product, brandSlug)) return;
    SPORTS.forEach(sport => {
      if (productBelongsToSport(product, sport)) {
        sportSlugs.add(sport.slug);
      }
    });
  });

  return SPORTS.filter(sport => sportSlugs.has(sport.slug));
}

/** Legacy helper — derive brand list from product assignments. */
export function getBrandsFromProducts(products) {
  const map = new Map();

  (products || []).forEach(product => {
    const slug = getProductBrandSlug(product);
    if (!slug || map.has(slug)) return;
    map.set(slug, {
      slug,
      name: slug
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
    });
  });

  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}
